/* ============================================================================
   OUDERA SET BUILDER — shared generic engine (v2)
   Custom element <oudera-set-builder>. No globals, no per-set logic.

   Every set is slot based. Each slot declares selection_mode:
     • simple_variant       -> choose product (auto-picks a single eligible variant)
     • product_then_variant -> choose product, THEN choose a size/variant chip

   Pricing = variant_price: final total = box price + each selected variant price,
   quantity always 1 (NO Tola multiplication). All money math is integer minor
   units; display currency (ISO code first, e.g. "AED 350.00") comes from Shopify.
   All product/variant rules come from the Liquid JSON config.
   ============================================================================ */
(function () {
  'use strict';

  if (window.customElements && customElements.get('oudera-set-builder')) return;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- Normalisation for allowed / excluded matching ---------- */
  function normalize(value, unit) {
    var v = String(value == null ? '' : value).toLowerCase().replace(/\s+/g, '');
    if (unit) v = v.split(String(unit).toLowerCase()).join('');
    return v;
  }
  function parseList(raw, unit) {
    if (!raw) return [];
    var out = [];
    String(raw).split(',').forEach(function (tok) {
      var n = normalize(tok, unit);
      if (n) out.push(n);
    });
    return out;
  }
  /* Display label for a variant chip: keep the real Shopify title, append the
     unit only when the title is a bare number (e.g. "8" -> "8 Tola"). */
  function chipLabel(title, unit) {
    var t = String(title == null ? '' : title).trim();
    if (unit && /^\d+(\.\d+)?$/.test(t)) return t + ' ' + unit;
    return t;
  }
  /* Compact size value for cart properties: strip the unit word from the title. */
  function sizeValue(title, unit) {
    var t = String(title == null ? '' : title).trim();
    if (!unit) return t;
    var stripped = t.replace(new RegExp('\\s*' + String(unit).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'ig'), '').trim();
    return stripped || t;
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  class OuderaSetBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      var cfgEl = this.querySelector('[data-gb-config]');
      if (!cfgEl) return;
      try { this.config = JSON.parse(cfgEl.textContent); }
      catch (e) { this.config = null; return; }

      this.bundleName = this.getAttribute('data-bundle-name') || this.config.bundleName || 'Set';
      this.strings = this.config.strings || {};

      /* ---- money: ISO code first, from Shopify's active currency ---- */
      this.currencyCode = this.getAttribute('data-currency') || 'AED';
      this._nf = null; this._digits = 2;
      try {
        this._nf = new Intl.NumberFormat('en', { style: 'currency', currency: this.currencyCode, currencyDisplay: 'code' });
        this._digits = this._nf.resolvedOptions().maximumFractionDigits;
      } catch (e) { this._nf = null; this._digits = 2; }

      // state
      this.selectedSize = null;
      this.selections = {};        // index -> { productId,title,image,variants[],variant|null,mode,unit,role }
      this.editing = null;         // { index }
      this.adding = false;
      this._lastFocus = null;

      // refs
      this.stepSlots = this.querySelector('[data-gb-step="slots"]');
      this.stepReview = this.querySelector('[data-gb-step="review"]');
      this.slotsEl = this.querySelector('[data-gb-slots]');
      this.summaryList = this.querySelector('[data-gb-summary]');
      this.totalEl = this.querySelector('[data-gb-total]');
      this.errorEl = this.querySelector('[data-gb-error]');
      this.addBtn = this.querySelector('[data-gb-add]');
      this.addLabel = this.querySelector('[data-gb-add-label]');

      this.modal = this.querySelector('[data-gb-modal]');
      this.modalPanel = this.querySelector('[data-gb-modal-panel]');
      this.modalTitle = this.querySelector('[data-gb-modal-title]');
      this.modalGrid = this.querySelector('[data-gb-modal-grid]');
      this.modalEmpty = this.querySelector('[data-gb-modal-empty]');
      this.searchInput = this.querySelector('[data-gb-search]');

      this.sticky = this.querySelector('[data-gb-sticky]');
      this.stickyTotal = this.querySelector('[data-gb-sticky-total]');
      this.stickyBtn = this.querySelector('[data-gb-sticky-add]');
      this.stickyLabel = this.querySelector('[data-gb-sticky-label]');

      this._onKeydown = this._onKeydown.bind(this);

      // repaint any server-rendered box prices in the active currency
      var self = this;
      this.querySelectorAll('[data-gb-box-price]').forEach(function (el) {
        el.textContent = self.formatMoney(parseInt(el.getAttribute('data-gb-box-price'), 10) || 0);
      });

      this._bindBoxes();
      this._bindSlots();
      this._bindModal();
      this._bindAdd();
    }

    /* ================= Money ================= */
    formatMoney(cents) {
      if (typeof cents === 'string') cents = parseInt(cents, 10);
      if (isNaN(cents) || cents == null) cents = 0;
      if (this._nf) {
        try { return this._nf.format(cents / Math.pow(10, this._digits)); } catch (e) {}
      }
      var amt = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.currencyCode + ' ' + amt;
    }

    /* ================= Box selection ================= */
    _bindBoxes() {
      var self = this;
      this.querySelectorAll('[data-gb-box]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          self._selectBox(btn.getAttribute('data-gb-box'));
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); }
        });
      });
    }

    _boxData() {
      var boxes = this.config.boxes || [];
      for (var i = 0; i < boxes.length; i++) if (boxes[i].size === this.selectedSize) return boxes[i];
      return null;
    }

    _slots() { return (this.config.slots && this.config.slots[this.selectedSize]) || []; }

    _selectBox(size) {
      var prevSize = this.selectedSize;
      this.selectedSize = size;

      this.querySelectorAll('[data-gb-box]').forEach(function (b) {
        var on = b.getAttribute('data-gb-box') === size;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      // remap compatible selections by signature (pools + group), re-filter for new size
      var prevSlots = prevSize ? this.config.slots[prevSize] : null;
      var prevSel = this.selections;
      var newSlots = this._slots();
      var carried = {};
      if (prevSlots) {
        var consumed = {};
        var self = this;
        newSlots.forEach(function (slot, i) {
          var sig = slot.pools.join('|') + '#' + (slot.group || '');
          for (var j = 0; j < prevSlots.length; j++) {
            if (consumed[j] || !prevSel[j]) continue;
            var ps = prevSlots[j];
            if (ps.pools.join('|') + '#' + (ps.group || '') === sig) {
              var moved = prevSel[j];
              // re-filter the carried product's variants for the new box size
              moved.variants = self._filterEligible(moved.allVariants, slot, size);
              // clear an invalid variant when switching Big/Large
              if (moved.variant && !self._variantAllowed(moved.variant, moved.variants)) moved.variant = null;
              moved.mode = slot.selectionMode; moved.unit = slot.unit; moved.role = slot.role;
              carried[i] = moved;
              consumed[j] = true;
              break;
            }
          }
        });
      }
      this.selections = carried;

      this._renderSlots();
      if (this.stepSlots) this.stepSlots.hidden = false;
      if (this.stepReview) this.stepReview.hidden = false;
      this._update();
    }

    /* ================= Variant eligibility ================= */
    _filterEligible(variants, slot, size) {
      if (!variants) return [];
      var unit = slot.unit || '';
      var allowedRaw = size === 'large' ? slot.allowedLargeRaw : slot.allowedBigRaw;
      var allowed = parseList(allowedRaw, unit);
      var excluded = parseList(slot.excludedRaw, unit);
      return variants.filter(function (v) {
        if (!v.available) return false;
        var n = normalize(v.title, unit);
        if (excluded.length && excluded.indexOf(n) > -1) return false;
        if (allowed.length && allowed.indexOf(n) === -1) return false;
        return true;
      });
    }
    _variantAllowed(variant, list) {
      for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(variant.id)) return true;
      return false;
    }
    _variantStock(v) {
      if (!v) return 0;
      if (!v.managed || v.policy === 'continue') return Infinity;
      var inv = parseInt(v.inventory, 10);
      return isNaN(inv) ? Infinity : Math.max(0, inv);
    }

    /* ================= Slot rendering ================= */
    _renderSlots() {
      var slots = this._slots();
      var frag = document.createDocumentFragment();
      var self = this;
      slots.forEach(function (slot, i) { frag.appendChild(self._buildSlot(slot, i)); });
      this.slotsEl.innerHTML = '';
      this.slotsEl.appendChild(frag);
    }

    _buildSlot(slot, index) {
      var sel = this.selections[index];
      var el = document.createElement('div');
      el.setAttribute('data-slot-index', String(index));

      var num = document.createElement('span');
      num.className = 'osb-slot__index';
      num.textContent = ('0' + (index + 1)).slice(-2);

      if (!sel) {
        el.className = 'osb-slot is-empty';
        el.appendChild(num);
        var m0 = document.createElement('div'); m0.className = 'osb-slot__main';
        var lb = document.createElement('span'); lb.className = 'osb-slot__label'; lb.textContent = slot.label; m0.appendChild(lb);
        var ht = document.createElement('span'); ht.className = 'osb-slot__hint'; ht.textContent = slot.help || ''; if (ht.textContent) m0.appendChild(ht);
        el.appendChild(m0);
        var a0 = document.createElement('div'); a0.className = 'osb-slot__actions';
        var b0 = this._btn('osb-slot__btn', { 'data-gb-open': index }, this.strings.select || 'Select');
        b0.setAttribute('aria-label', slot.label + ' — ' + (this.strings.select || 'Select'));
        a0.appendChild(b0); el.appendChild(a0);
        return el;
      }

      // product chosen
      var thumb = document.createElement('span'); thumb.className = 'osb-slot__thumb';
      if (sel.image) { var im = document.createElement('img'); im.src = sel.image; im.alt = sel.title; im.loading = 'lazy'; thumb.appendChild(im); }

      var main = document.createElement('div'); main.className = 'osb-slot__main';
      var nm = document.createElement('span'); nm.className = 'osb-slot__name'; nm.textContent = sel.title; main.appendChild(nm);

      if (sel.variant) {
        // FILLED
        el.className = 'osb-slot is-filled';
        el.appendChild(num); el.appendChild(thumb);
        if (sel.variant.hasTitle) {
          var vt = document.createElement('span'); vt.className = 'osb-slot__variant'; vt.textContent = chipLabel(sel.variant.title, sel.unit); main.appendChild(vt);
        }
        var pr = document.createElement('span'); pr.className = 'osb-slot__price'; pr.textContent = this.formatMoney(sel.variant.price); main.appendChild(pr);
        el.appendChild(main);
        var actions = document.createElement('div'); actions.className = 'osb-slot__actions';
        actions.appendChild(this._btn('osb-slot__btn', { 'data-gb-open': index }, this.strings.changeProduct || 'Change product'));
        if (sel.variants.length > 1) actions.appendChild(this._btn('osb-slot__btn', { 'data-gb-changesize': index }, this.strings.changeSize || 'Change size'));
        actions.appendChild(this._btn('osb-slot__btn osb-slot__btn--remove', { 'data-gb-remove': index }, this.strings.remove || 'Remove'));
        el.appendChild(actions);
        return el;
      }

      // PRODUCT CHOSEN, awaiting variant -> show chips
      el.className = 'osb-slot is-choosing';
      el.appendChild(num); el.appendChild(thumb);
      var hint = document.createElement('span'); hint.className = 'osb-slot__hint'; hint.textContent = this.strings.chooseSize || 'Choose a size to continue'; main.appendChild(hint);
      el.appendChild(main);

      var chips = document.createElement('div');
      chips.className = 'osb-slot__chips';
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-label', slot.label);
      var taken = this._takenVariantIds(index, slot.group);
      var self = this;
      sel.variants.forEach(function (v) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'osb-chip';
        chip.setAttribute('data-gb-variant', String(v.id));
        chip.setAttribute('role', 'radio');
        chip.setAttribute('aria-checked', 'false');
        var stock = self._variantStock(v);
        if (taken[v.id] || stock <= 0) { chip.disabled = true; chip.setAttribute('aria-disabled', 'true'); }
        var lab = document.createElement('span'); lab.textContent = chipLabel(v.title, sel.unit); chip.appendChild(lab);
        var pc = document.createElement('span'); pc.className = 'osb-chip__price'; pc.textContent = self.formatMoney(v.price); chip.appendChild(pc);
        chips.appendChild(chip);
      });
      el.appendChild(chips);

      var a2 = document.createElement('div'); a2.className = 'osb-slot__actions';
      a2.appendChild(this._btn('osb-slot__btn', { 'data-gb-open': index }, this.strings.changeProduct || 'Change product'));
      a2.appendChild(this._btn('osb-slot__btn osb-slot__btn--remove', { 'data-gb-remove': index }, this.strings.remove || 'Remove'));
      el.appendChild(a2);
      return el;
    }

    _btn(cls, attrs, text) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = cls; b.textContent = text;
      for (var k in attrs) if (attrs.hasOwnProperty(k)) b.setAttribute(k, String(attrs[k]));
      return b;
    }

    _bindSlots() {
      var self = this;
      this.slotsEl.addEventListener('click', function (e) {
        var open = e.target.closest('[data-gb-open]');
        var remove = e.target.closest('[data-gb-remove]');
        var changeSize = e.target.closest('[data-gb-changesize]');
        var chip = e.target.closest('[data-gb-variant]');
        if (open) { self._openModal(parseInt(open.getAttribute('data-gb-open'), 10)); return; }
        if (remove) { delete self.selections[parseInt(remove.getAttribute('data-gb-remove'), 10)]; self._renderSlots(); self._update(); return; }
        if (changeSize) {
          var idx = parseInt(changeSize.getAttribute('data-gb-changesize'), 10);
          if (self.selections[idx]) { self.selections[idx].variant = null; self._renderSlots(); self._update(); }
          return;
        }
        if (chip && !chip.disabled) {
          var slotEl = chip.closest('[data-slot-index]');
          var i = parseInt(slotEl.getAttribute('data-slot-index'), 10);
          self._pickVariant(i, chip.getAttribute('data-gb-variant'));
        }
      });
    }

    _pickVariant(index, variantId) {
      var sel = this.selections[index];
      if (!sel) return;
      for (var i = 0; i < sel.variants.length; i++) {
        if (String(sel.variants[i].id) === String(variantId)) { sel.variant = sel.variants[i]; break; }
      }
      this._renderSlots();
      this._update();
    }

    _takenVariantIds(currentIndex, group) {
      var out = {};
      if (!group) return out;
      var slots = this._slots();
      for (var i = 0; i < slots.length; i++) {
        if (i === currentIndex || slots[i].group !== group) continue;
        var sel = this.selections[i];
        if (sel && sel.variant) out[sel.variant.id] = true;
      }
      return out;
    }

    /* ================= Modal (product cards) ================= */
    _bindModal() {
      var self = this;
      this.modal.querySelectorAll('[data-gb-modal-close]').forEach(function (el) {
        el.addEventListener('click', function () { self._closeModal(); });
      });
      this.modalGrid.addEventListener('click', function (e) {
        var card = e.target.closest('[data-gb-option]');
        if (!card || card.classList.contains('is-hidden')) return;
        self._chooseProduct(card);
      });
      this.searchInput.addEventListener('input', function () { self._filter(this.value); });
    }

    _openModal(index) {
      this.editing = { index: index };
      this._lastFocus = document.activeElement;

      var slot = this._slots()[index];
      var current = this.selections[index];
      var taken = this._takenVariantIds(index, slot.group);

      this.modalTitle.textContent = slot.label;
      this.searchInput.value = '';
      this.modalGrid.innerHTML = '';

      var seen = {}, self = this, rendered = 0;
      (slot.pools || []).forEach(function (poolKey) {
        var tpl = self.querySelector('template[data-gb-pool="' + poolKey + '"]');
        if (!tpl) return;
        tpl.content.querySelectorAll('[data-gb-option]').forEach(function (node) {
          var pid = node.getAttribute('data-product-id');
          if (seen[pid]) return;
          seen[pid] = true;

          var all = self._readVariants(node);
          var eligible = self._filterEligible(all, slot, self.selectedSize);
          // remove variants already taken in this duplicate group (unless it's the current product)
          var isCurrent = current && String(current.productId) === String(pid);
          var usable = eligible.filter(function (v) { return isCurrent || !taken[v.id]; });
          if (!usable.length) return; // hide products with no eligible/available variant

          var clone = node.cloneNode(true);
          // AED price: "From X" when variants differ, else exact
          var min = Infinity, max = -Infinity;
          usable.forEach(function (v) { if (v.price < min) min = v.price; if (v.price > max) max = v.price; });
          var priceEl = clone.querySelector('[data-gb-card-price]');
          if (priceEl) priceEl.textContent = (min < max ? (self.strings.from || 'From') + ' ' : '') + self.formatMoney(min);
          if (isCurrent) clone.setAttribute('aria-selected', 'true');
          self.modalGrid.appendChild(clone);
          rendered++;
        });
      });
      this.modalEmpty.hidden = rendered > 0;

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('osb-no-scroll', 'osb-modal-open');
      document.addEventListener('keydown', this._onKeydown);
      var panel = this.modalPanel;
      requestAnimationFrame(function () { panel.focus({ preventScroll: true }); });
    }

    _readVariants(node) {
      var script = node.querySelector('script[data-gb-variants]');
      if (!script) return [];
      try { return JSON.parse(script.textContent) || []; } catch (e) { return []; }
    }

    _closeModal() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('osb-no-scroll', 'osb-modal-open');
      document.removeEventListener('keydown', this._onKeydown);
      var focus = this._lastFocus;
      this.editing = null;
      if (focus && focus.focus) { try { focus.focus(); } catch (e) {} }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') { e.stopPropagation(); this._closeModal(); return; }
      if (e.key === 'Tab') {
        var f = this.modalPanel.querySelectorAll(FOCUSABLE);
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    _filter(q) {
      q = (q || '').trim().toLowerCase();
      var visible = 0;
      this.modalGrid.querySelectorAll('[data-gb-option]').forEach(function (c) {
        var hay = ((c.getAttribute('data-title') || '') + ' ' + (c.getAttribute('data-collection') || '')).toLowerCase();
        var show = !q || hay.indexOf(q) > -1;
        c.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      this.modalEmpty.hidden = visible > 0;
    }

    _chooseProduct(card) {
      var ctx = this.editing;
      if (!ctx) return;
      var index = ctx.index;
      var slot = this._slots()[index];
      var all = this._readVariants(card);
      var eligible = this._filterEligible(all, slot, this.selectedSize);
      var taken = this._takenVariantIds(index, slot.group);
      var usable = eligible.filter(function (v) { return !taken[v.id]; });
      if (!usable.length) { this._closeModal(); return; }

      var sel = {
        productId: card.getAttribute('data-product-id'),
        title: card.getAttribute('data-title') || '',
        image: card.getAttribute('data-image') || '',
        collection: card.getAttribute('data-collection') || '',
        allVariants: all,
        variants: usable,
        variant: null,
        mode: slot.selectionMode,
        unit: slot.unit || '',
        role: slot.role
      };

      // Auto-select when a single eligible variant exists (keep label if meaningful).
      if (usable.length === 1 && (slot.selectionMode === 'simple_variant' || usable.length === 1)) {
        sel.variant = usable[0];
      }
      this.selections[index] = sel;
      this._closeModal();
      this._renderSlots();
      this._update();
    }

    /* ================= Summary + total + button ================= */
    _update() {
      var box = this._boxData();
      var slots = this._slots();
      var rows = [];
      var total = 0;

      if (box) { total += box.price; rows.push({ name: this.bundleName, sub: box.label, price: box.price }); }

      var complete = slots.length > 0 && !!box;
      for (var i = 0; i < slots.length; i++) {
        var sel = this.selections[i];
        var required = slots[i].required !== false;
        if (!sel || !sel.variant) { if (required) complete = false; continue; }
        total += sel.variant.price;
        var sub = sel.variant.hasTitle ? chipLabel(sel.variant.title, sel.unit) : slots[i].label;
        rows.push({ name: sel.title, sub: sub, price: sel.variant.price });
      }

      // render summary
      this.summaryList.innerHTML = '';
      var self = this;
      rows.forEach(function (r) {
        var li = document.createElement('li'); li.className = 'osb__summary-row';
        var nameWrap = document.createElement('span'); nameWrap.className = 'osb__summary-row-name';
        nameWrap.appendChild(document.createTextNode(r.name));
        if (r.sub) { var small = document.createElement('small'); small.textContent = r.sub; nameWrap.appendChild(small); }
        var priceEl = document.createElement('span'); priceEl.className = 'osb__summary-row-price'; priceEl.textContent = self.formatMoney(r.price);
        li.appendChild(nameWrap); li.appendChild(priceEl);
        self.summaryList.appendChild(li);
      });

      if (isNaN(total)) total = 0;
      var totalStr = this.formatMoney(total);
      this.totalEl.textContent = totalStr;
      if (this.stickyTotal) this.stickyTotal.textContent = totalStr;

      this._setButton(complete, totalStr);

      if (this.sticky) {
        this.sticky.hidden = !this.selectedSize;
        this._reserveSticky();
      }
    }

    _reserveSticky() {
      var self = this;
      requestAnimationFrame(function () {
        var h = (self.sticky && !self.sticky.hidden) ? self.sticky.offsetHeight : 0;
        self.style.setProperty('--osb-sticky-h', h + 'px');
      });
    }

    _setButton(ready, totalStr) {
      var full = (this.strings.addPrefix || 'Add complete set') + ' — ' + totalStr;
      var incomplete = this.strings.incomplete || 'Complete your selections';
      if (this.addLabel) this.addLabel.textContent = ready ? full : incomplete;
      if (this.addBtn) this.addBtn.disabled = !ready || this.adding;
      if (this.stickyLabel) this.stickyLabel.textContent = ready ? (this.strings.addPrefix || 'Add complete set') : incomplete;
      if (this.stickyBtn) this.stickyBtn.disabled = !ready || this.adding;
    }

    /* ================= Add to cart ================= */
    _bindAdd() {
      var self = this;
      if (this.addBtn) this.addBtn.addEventListener('click', function () { self._add(); });
      if (this.stickyBtn) this.stickyBtn.addEventListener('click', function () { self._add(); });
    }

    _showError(msg) { if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.hidden = !msg; } }

    _setLoading(on) {
      this.adding = on;
      if (this.addBtn) { this.addBtn.classList.toggle('is-loading', on); this.addBtn.disabled = on; }
      if (this.stickyBtn) this.stickyBtn.disabled = on;
      if (on && this.addLabel) this.addLabel.textContent = this.strings.adding || 'Adding…';
    }

    _buildItems(bundleId, box) {
      var bundleFull = this.bundleName + ' — ' + box.label;
      var common = { _bundle_id: bundleId, _bundle_name: bundleFull, _box_size: box.label };
      var items = [];
      items.push({ id: box.variantId, quantity: 1, properties: Object.assign({}, common, { _bundle_role: this.strings.boxLabel || 'Box' }) });

      var slots = this._slots();
      for (var i = 0; i < slots.length; i++) {
        var sel = this.selections[i];
        if (!sel || !sel.variant) continue;
        var props = Object.assign({}, common, {
          _bundle_role: sel.role,
          _selected_product: sel.title,
          _selected_variant: sel.variant.title
        });
        if (sel.variant.hasTitle) props._selected_size = sizeValue(sel.variant.title, sel.unit);
        if (sel.unit) props._measurement_unit = sel.unit;
        items.push({ id: parseInt(sel.variant.id, 10), quantity: 1, properties: props });
      }
      return items;
    }

    _add() {
      if (this.adding) return;
      var box = this._boxData();
      if (!box) return;

      var slots = this._slots();
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].required !== false && (!this.selections[i] || !this.selections[i].variant)) return;
      }
      // stock guard (quantity 1)
      for (var k = 0; k < slots.length; k++) {
        var s = this.selections[k];
        if (s && s.variant && this._variantStock(s.variant) < 1) { this._showError(this.strings.outOfStock || 'Out of stock.'); return; }
      }

      this._showError('');
      this._setLoading(true);

      var bundleId = 'osb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var items = this._buildItems(bundleId, box);

      var drawer = document.querySelector('cart-drawer-premium');
      var sectionIds = drawer && drawer.getSectionsToRender
        ? drawer.getSectionsToRender().map(function (x) { return x.id; }).join(',') : '';

      var body = { items: items };
      if (sectionIds) { body.sections = sectionIds; body.sections_url = window.location.pathname; }

      var self = this;
      fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) {
            self._setLoading(false); self._update();
            self._showError(res.data.description || res.data.message || self.strings.unavailable || 'Some items are unavailable.');
            return;
          }
          self.adding = false; self._setLoading(false); self._update();
          var opened = false;
          if (drawer && res.data.sections) { try { drawer.renderContents(res.data); opened = true; } catch (e) {} }
          if (window.publish && window.PUB_SUB_EVENTS) {
            try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'set-builder', cartData: res.data }); } catch (e) {}
          }
          if (drawer && drawer.open && !opened) { try { drawer.open(); } catch (e) {} }
        })
        .catch(function () {
          self._setLoading(false); self._update();
          self._showError(self.strings.genericError || 'Something went wrong. Please try again.');
        });
    }
  }

  customElements.define('oudera-set-builder', OuderaSetBuilder);
})();
